import { CreditCard, Loader2, Lock, Users, HardDrive, Check, ArrowLeft } from 'lucide-react';
import { PLANS } from './loginConstants';

const LoginTrialExpiredSection = ({
  trialExpiredData, t, language,
  billingCycle, setBillingCycle,
  selectedPlan, setSelectedPlan,
  paymentLoading, paymentError,
  handleSubscribe, setTrialExpiredData,
}) => {
  const isSuspended = trialExpiredData.code === 'account_suspended';

  return (
    <div className="mb-6 space-y-5">
      {/* Status banner */}
      <div className="p-5 rounded-2xl text-center" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid #23252a" }}>
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
          style={{ backgroundColor: isSuspended ? "rgba(239,68,68,0.1)" : "rgba(94,106,210,0.12)", border: `1px solid ${isSuspended ? "rgba(239,68,68,0.3)" : "rgba(94,106,210,0.3)"}` }}>
          {isSuspended
            ? <Lock className="w-6 h-6" style={{ color: "#ef4444" }} />
            : <CreditCard className="w-6 h-6" style={{ color: "#5e6ad2" }} />
          }
        </div>
        <h2 className="text-base font-semibold mb-1" style={{ color: "var(--lm-ink)", letterSpacing: "-0.3px" }}>
          {isSuspended ? t.accountSuspendedTitle : t.trialExpiredTitle}
        </h2>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
          {isSuspended ? t.accountSuspendedMessage : t.trialExpiredMessage}
        </p>
        {trialExpiredData.trial_end_date && (
          <p className="text-xs mt-2" style={{ color: "var(--lm-ink-3)" }}>
            {t.trialEndedOn} :{' '}
            {new Date(trialExpiredData.trial_end_date).toLocaleDateString(
              language === 'ar' ? 'ar-TN' : language === 'en' ? 'en-GB' : 'fr-FR'
            )}
          </p>
        )}
      </div>

      {/* Billing toggle */}
      <div className="flex justify-center">
        <div className="inline-flex p-1 rounded-full" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #23252a" }}>
          {[{ value: 'monthly', label: t.billingMonthly }, { value: 'annual', label: t.billingAnnual }].map(({ value, label }) => (
            <button key={value} type="button" onClick={() => setBillingCycle(value)}
              className="px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2"
              style={{
                backgroundColor: billingCycle === value ? "var(--lm-s3)" : "transparent",
                color: billingCycle === value ? "var(--lm-ink)" : "var(--lm-ink-2)",
                border: billingCycle === value ? "1px solid #34343a" : "1px solid transparent",
              }}>
              {label}
              {value === 'annual' && (
                <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(39,166,68,0.15)", color: "#27a644" }}>-17%</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Payment error */}
      {paymentError && (
        <div className="text-sm p-3 rounded-xl" style={{ backgroundColor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444" }}>
          {paymentError}
        </div>
      )}

      {/* Gateway selection */}
      {selectedPlan ? (
        <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid #23252a" }}>
          <div className="p-4" style={{ borderBottom: "1px solid #23252a" }}>
            <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>
              {selectedPlan.name} — {billingCycle === 'monthly' ? selectedPlan.monthlyDT : selectedPlan.annualDT} {billingCycle === 'monthly' ? t.paymentPerMonth : t.paymentPerYear}
            </p>
            <p className="text-xs mt-0.5" style={{ color: "var(--lm-ink-2)" }}>{t.choosePaymentMethod}</p>
          </div>
          <div className="p-4 space-y-3">
            {[
              { id: 'stripe',  label: 'Stripe',  badge: 'International', desc: 'Visa, Mastercard, American Express, Apple Pay', accent: "#5e6ad2" },
            ].map(({ id, label, badge, desc, accent }) => (
              <button key={id} onClick={() => handleSubscribe(selectedPlan.code, id)}
                disabled={paymentLoading !== null}
                className="w-full flex items-center gap-4 p-4 rounded-xl transition-all text-left disabled:opacity-60"
                style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #23252a" }}
                onMouseEnter={e => { if (!paymentLoading) e.currentTarget.style.borderColor = "var(--lm-border-2)"; }}
                onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--lm-border)")}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${accent}18`, border: `1px solid ${accent}40` }}>
                  {paymentLoading === `${selectedPlan.code}-${id}`
                    ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: accent }} />
                    : <CreditCard className="w-4 h-4" style={{ color: accent }} />
                  }
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>
                    {label} <span className="text-xs font-normal" style={{ color: "var(--lm-ink-2)" }}>{badge}</span>
                  </p>
                  <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{desc}</p>
                </div>
              </button>
            ))}
            <button onClick={() => setSelectedPlan(null)}
              className="w-full flex items-center gap-1.5 justify-center text-xs pt-1 transition-colors"
              style={{ color: "var(--lm-ink-3)" }}
              onMouseEnter={e => (e.currentTarget.style.color = "var(--lm-ink-2)")}
              onMouseLeave={e => (e.currentTarget.style.color = "var(--lm-ink-3)")}>
              <ArrowLeft className="w-3.5 h-3.5" /> {t.backToPlans}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            {PLANS.map((plan) => (
              <div key={plan.code}
                className="relative p-4 rounded-xl flex flex-col"
                style={{
                  backgroundColor: "var(--lm-s1)",
                  border: `1px solid ${plan.popular ? "#5e6ad2" : "var(--lm-border)"}`,
                }}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full" style={{ backgroundColor: "#5e6ad2", color: "#fff" }}>{t.popularPlan}</span>
                  </div>
                )}
                <p className="text-sm font-semibold mb-2" style={{ color: "var(--lm-ink)" }}>{plan.name}</p>
                <div className="mb-3">
                  <span className="text-xl font-bold" style={{ color: "var(--lm-ink)" }}>
                    {billingCycle === 'monthly' ? plan.monthlyDT : Math.round(plan.annualDT / 12)}
                  </span>
                  <span className="text-xs ml-1" style={{ color: "var(--lm-ink-2)" }}>{t.paymentPerMonth}</span>
                  {billingCycle === 'annual' && (
                    <p className="text-xs mt-0.5" style={{ color: "#27a644" }}>{plan.annualDT} {t.paymentPerYear}</p>
                  )}
                </div>
                <div className="space-y-1 mb-3 text-xs" style={{ color: "var(--lm-ink-3)" }}>
                  <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {plan.employees[language] || plan.employees.fr}</div>
                  <div className="flex items-center gap-1.5"><HardDrive className="w-3.5 h-3.5" /> {plan.storage[language] || plan.storage.fr}</div>
                </div>
                <ul className="space-y-1 mb-4 flex-1">
                  {(plan.features[language] || plan.features.fr).map((f) => (
                    <li key={f} className="flex items-start gap-1.5 text-xs" style={{ color: "var(--lm-ink-2)" }}>
                      <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: "#27a644" }} />{f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => setSelectedPlan(plan)}
                  className="w-full py-2 rounded-lg text-xs font-medium transition-colors"
                  style={{ backgroundColor: plan.popular ? "#5e6ad2" : "var(--lm-s2)", color: "#fff", border: `1px solid ${plan.popular ? "#5e6ad2" : "var(--lm-border-2)"}` }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = plan.popular ? "#828fff" : "var(--lm-s3)"; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = plan.popular ? "#5e6ad2" : "var(--lm-s2)"; }}>
                  {t.pay}
                </button>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => setTrialExpiredData(null)}
              className="text-xs transition-colors"
              style={{ color: "var(--lm-ink-3)" }}
              onMouseEnter={e => (e.currentTarget.style.color = "var(--lm-ink-2)")}
              onMouseLeave={e => (e.currentTarget.style.color = "var(--lm-ink-3)")}>
              ← {t.backToLogin}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LoginTrialExpiredSection;
